/**
 * AudioRecorder Component
 * Records audio responses for screening questions
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  Mic, 
  Square, 
  Play, 
  Pause, 
  RotateCcw, 
  Upload,
  AlertCircle,
  CheckCircle,
  Loader
} from 'lucide-react';
import { startRecording, stopRecording, uploadAudioRecording } from '../services/audioService';
import { useAuth } from '../../../core/contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * AudioRecorder Component
 * 
 * @param {string} questionId - ID of the screening question
 * @param {string} questionText - Text of the screening question
 * @param {function} onRecordingComplete - Callback when recording is uploaded
 * @param {number} maxDuration - Maximum recording duration in seconds (default: 120)
 * @returns {JSX.Element} AudioRecorder component
 */
const AudioRecorder = ({ 
  questionId, 
  questionText, 
  onRecordingComplete, 
  maxDuration = 120 
}) => {
  const { currentUser } = useAuth();
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [error, setError] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Start recording timer
  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= maxDuration) {
          handleStopRecording();
          return prev;
        }
        return prev + 1;
      });
    }, 1000);
  };

  // Stop recording timer
  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle start recording
  const handleStartRecording = async () => {
    try {
      setError(null);
      
      const { mediaRecorder, stream, audioChunks } = await startRecording();
      
      mediaRecorderRef.current = mediaRecorder;
      streamRef.current = stream;
      audioChunksRef.current = audioChunks;
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      startTimer();
      
      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording. Please check microphone permissions.');
      toast.error('Failed to start recording');
    }
  };

  // Handle stop recording
  const handleStopRecording = async () => {
    if (!mediaRecorderRef.current || !streamRef.current) return;

    try {
      const blob = await stopRecording(
        mediaRecorderRef.current, 
        streamRef.current, 
        audioChunksRef.current
      );
      
      setAudioBlob(blob);
      setIsRecording(false);
      setHasRecording(true);
      stopTimer();
      
      toast.success('Recording completed');
    } catch (error) {
      console.error('Error stopping recording:', error);
      setError('Failed to stop recording');
      toast.error('Failed to stop recording');
    }
  };

  // Handle pause/resume recording
  const handlePauseResume = () => {
    if (!mediaRecorderRef.current) return;

    if (isPaused) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer();
    } else {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      stopTimer();
    }
  };

  // Handle play/pause audio playback
  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  // Handle reset recording
  const handleReset = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    setAudioBlob(null);
    setHasRecording(false);
    setIsPlaying(false);
    setRecordingTime(0);
    setError(null);
    stopTimer();
  };

  // Handle upload recording
  const handleUpload = async () => {
    if (!audioBlob || !currentUser) return;

    try {
      setIsUploading(true);
      setError(null);

      const result = await uploadAudioRecording(
        audioBlob, 
        currentUser.uid, 
        questionId, 
        recordingTime
      );

      toast.success('Recording uploaded successfully!');
      
      if (onRecordingComplete) {
        onRecordingComplete(result);
      }

      // Reset after successful upload
      handleReset();
      
    } catch (error) {
      console.error('Error uploading recording:', error);
      setError(error.message || 'Failed to upload recording');
      toast.error('Failed to upload recording');
    } finally {
      setIsUploading(false);
    }
  };

  // Create audio URL when blob is ready
  useEffect(() => {
    if (audioBlob && audioRef.current) {
      const audioUrl = URL.createObjectURL(audioBlob);
      audioRef.current.src = audioUrl;
      
      return () => {
        URL.revokeObjectURL(audioUrl);
      };
    }
  }, [audioBlob]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Get recording status color
  const getStatusColor = () => {
    if (error) return 'text-red-600';
    if (isRecording) return 'text-red-500';
    if (hasRecording) return 'text-green-600';
    return 'text-gray-600';
  };

  // Get progress bar width
  const getProgressWidth = () => {
    return Math.min((recordingTime / maxDuration) * 100, 100);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      {/* Question */}
      <div className="mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Screening Question
        </h3>
        <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
          {questionText}
        </p>
      </div>

      {/* Recording Status */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-sm font-medium ${getStatusColor()}`}>
            {error && 'Error'}
            {!error && isRecording && (isPaused ? 'Paused' : 'Recording...')}
            {!error && !isRecording && hasRecording && 'Recording Complete'}
            {!error && !isRecording && !hasRecording && 'Ready to Record'}
          </span>
          <span className="text-sm text-gray-500">
            {formatTime(recordingTime)} / {formatTime(maxDuration)}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              isRecording ? 'bg-red-500' : hasRecording ? 'bg-green-500' : 'bg-gray-300'
            }`}
            style={{ width: `${getProgressWidth()}%` }}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
          <span className="text-sm text-red-700">{error}</span>
        </div>
      )}

      {/* Recording Controls */}
      <div className="flex items-center justify-center space-x-4 mb-6">
        {!isRecording && !hasRecording && (
          <button
            onClick={handleStartRecording}
            className="flex items-center justify-center w-16 h-16 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
            title="Start Recording"
          >
            <Mic className="w-6 h-6" />
          </button>
        )}

        {isRecording && (
          <>
            <button
              onClick={handlePauseResume}
              className="flex items-center justify-center w-12 h-12 bg-yellow-500 hover:bg-yellow-600 text-white rounded-full transition-colors"
              title={isPaused ? "Resume" : "Pause"}
            >
              {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
            </button>

            <button
              onClick={handleStopRecording}
              className="flex items-center justify-center w-16 h-16 bg-gray-600 hover:bg-gray-700 text-white rounded-full transition-colors"
              title="Stop Recording"
            >
              <Square className="w-6 h-6" />
            </button>
          </>
        )}

        {hasRecording && (
          <>
            <button
              onClick={handlePlayPause}
              className="flex items-center justify-center w-12 h-12 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
              title={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
            </button>

            <button
              onClick={handleReset}
              className="flex items-center justify-center w-12 h-12 bg-gray-500 hover:bg-gray-600 text-white rounded-full transition-colors"
              title="Reset"
            >
              <RotateCcw className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Audio Player */}
      {hasRecording && (
        <div className="mb-6">
          <audio
            ref={audioRef}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            className="w-full"
            controls
          />
        </div>
      )}

      {/* Upload Button */}
      {hasRecording && (
        <div className="flex justify-center">
          <button
            onClick={handleUpload}
            disabled={isUploading}
            className={`flex items-center px-6 py-3 rounded-lg text-white font-medium transition-colors ${
              isUploading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {isUploading ? (
              <>
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-5 h-5 mr-2" />
                Upload Recording
              </>
            )}
          </button>
        </div>
      )}

      {/* Success Message */}
      {!hasRecording && !isRecording && !error && recordingTime > 0 && (
        <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <p className="text-sm text-green-700">Recording uploaded successfully!</p>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Recording Tips:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Speak clearly and at a moderate pace</li>
          <li>• Find a quiet environment to minimize background noise</li>
          <li>• Take a moment to think before you start speaking</li>
          <li>• Keep your answer concise and relevant</li>
          <li>• You can re-record if you're not satisfied</li>
        </ul>
      </div>
    </div>
  );
};

export default AudioRecorder;